import { useMutation } from '@apollo/client';
import { useContext } from 'react';
import { FileContext } from '../context/FileContext';
import { CreateFilesResponse, CreateFilesVariables, FileMutations } from '../graphql/file.graphql';
import { FileEntityType, FileEntity, CreateFileInput } from '../types/file';
import { CompanyContext } from '../context/CompanyContext';

export function useFileUpload({
  entityId,
  entityType,
  customFieldId,
  setError,
}: {
  entityId: string;
  entityType: FileEntityType;
  customFieldId: string;
  setError: (msg: string) => void;
}) {
  const { files, setFiles } = useContext(FileContext);
  const { currentCompany } = useContext(CompanyContext);

  const [create] = useMutation<CreateFilesResponse, CreateFilesVariables>(FileMutations.create, {
    onCompleted: result => {
      result.createFiles.forEach(file => files.set(file.id, new FileEntity(file)));
      setFiles(new Map(files));
    },
    onError: error => setError(error.message),
  });

  const uploadFiles = async (fileList: File[] | null): Promise<string[]> => {
    if (!fileList?.length) {
      setError('Something went wrong uploading your file!');
      return [];
    }

    const inputs: CreateFileInput[] = await Promise.all(
      fileList.map(async file => {
        const f = await CreateFileInput.fromFile(file, entityId, entityType, currentCompany.id);
        f.path = `${entityType}/${entityId}/${customFieldId}`;
        return f;
      }),
    );
    const result = await create({ variables: { files: inputs } });
    return result?.data?.createFiles?.map(file => file.id) || [];
  };

  return { uploadFiles };
}
